const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

function storageAvailable(type) {
  if (!isBrowser) return false;

  try {
    const storage = window[type];
    if (!storage) return false;
    const key = '__storage_test__';
    storage.setItem(key, 'ok');
    storage.removeItem(key);
    return true;
  } catch (e) {
    return false;
  }
}

function setLargeCookie(name, value, options = {}) {
  if (!isBrowser) return false;

  const encoded = encodeURIComponent(value);
  const maxChunkSize = 3800; // Leave some room for cookie metadata

  if (encoded.length <= maxChunkSize) {
    return setCookie(name, value, options);
  }

  const chunks = [];
  for (let i = 0; i < encoded.length; i += maxChunkSize) {
    chunks.push(encoded.slice(i, i + maxChunkSize));
  }

  setCookie(`${name}_chunks`, chunks.length.toString(), options);

  chunks.forEach((chunk, index) => {
    setCookie(`${name}_${index}`, decodeURIComponent(chunk), options);
  });

  return true;
}

function getLargeCookie(name) {
  if (!isBrowser) return null;

  const chunkCount = parseInt(getCookie(`${name}_chunks`));
  if (!chunkCount) {
    return getCookie(name);
  }

  let result = '';
  for (let i = 0; i < chunkCount; i++) {
    const chunk = getCookie(`${name}_${i}`);
    if (chunk === null) return null; // Missing chunk
    result += encodeURIComponent(chunk);
  }

  return decodeURIComponent(result);
}

function deleteLargeCookie(name, { path = '/' } = {}) {
  if (!isBrowser) return;

  const chunkCount = parseInt(getCookie(`${name}_chunks`));

  if (chunkCount) {
    for (let i = 0; i < chunkCount; i++) {
      deleteCookie(`${name}_${i}`, { path });
    }

    deleteCookie(`${name}_chunks`, { path });
  } else {
    deleteCookie(name, { path });
  }
}

function setCookie(name, value, { days, path = '/', sameSite = 'Lax', secure } = {}) {
  if (!isBrowser) return;
  let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; path=${path}; SameSite=${sameSite}`;
  if (secure === true) cookie += '; Secure';
  if (typeof days === 'number') {
    const d = new Date();
    d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
    cookie += `; Expires=${d.toUTCString()}`;
  }
  document.cookie = cookie;
}

function getCookie(name) {
  if (!isBrowser) return null;
  const nameEQ = encodeURIComponent(name) + '=';
  const parts = document.cookie.split('; ');
  for (const part of parts) {
    if (part.indexOf(nameEQ) === 0) {
      return decodeURIComponent(part.substring(nameEQ.length));
    }
  }
  return null;
}

function deleteCookie(name, { path = '/' } = {}) {
  if (!isBrowser) return;
  document.cookie = `${encodeURIComponent(name)}=; Expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`;
}

const memoryStore = (() => {
  const map = new Map();
  return {
    getItem: (k) => (map.has(k) ? map.get(k) : null),
    setItem: (k, v) => {
      map.set(k, v);
    },
    removeItem: (k) => {
      map.delete(k);
    }
  };
})();

function makeCookieAdapter({ persistentDays = 365 } = {}) {
  return {
    getItem: (k) => getLargeCookie(k),
    setItem: (k, v) =>
      setLargeCookie(k, v, { days: persistentDays, secure: location.protocol === 'https:' }),
    removeItem: (k) => deleteLargeCookie(k)
  };
}

function makeSessionCookieAdapter() {
  return {
    getItem: (k) => getLargeCookie(k),
    setItem: (k, v) => setLargeCookie(k, v, { secure: location.protocol === 'https:' }),
    removeItem: (k) => deleteLargeCookie(k)
  };
}

export function chooseBackend(kind /* "local" | "session" */) {
  if (kind === 'local') {
    if (storageAvailable('localStorage')) return window.localStorage;
    if (storageAvailable('sessionStorage')) return window.sessionStorage;

    return makeCookieAdapter({ persistentDays: 365 });
  } else {
    if (storageAvailable('sessionStorage')) return window.sessionStorage;
    if (storageAvailable('localStorage')) return window.localStorage;

    return makeSessionCookieAdapter();
  }
}

export const memoryBackend = memoryStore;
export const isRuntimeBrowser = isBrowser;
